import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, useHistory, useLocation, useParams } from 'react-router-dom';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import { Helmet } from 'react-helmet-async';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { ensureListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './BookingFlowPage.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import {
  MIXPANEL_EVENTS,
  listingMixpanelProperties,
  trackMixpanelEvent,
} from '../../util/mixpanel';
import { clearReservationContactSession } from '../../util/bookingResume';
import {
  optimizeCloudinaryUrl,
  getCloudinarySrcSet,
  DESKTOP_HERO_SRCSET_WIDTHS,
  TABLET_SRCSET_WIDTHS,
  MOBILE_SRCSET_WIDTHS,
} from '../../util/cloudinaryHelpers';

const { UUID } = sdkTypes;

/**
 * BookingFlowPage - Shows booking options for the listing
 *
 * @component
 */
const BookingFlowPageComponent = props => {
  const { getListing } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const optionsViewedAtRef = useRef(null);
  const actionSelectedRef = useRef(false);
  const abandonmentTrackedRef = useRef(false);
  const cardsWrapperRef = useRef(null);

  const scrollToForm = ref => {
    if (ref && ref.current && typeof window !== 'undefined') {
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  };

  // Scroll to cards when page mounts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        scrollToForm(cardsWrapperRef);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  // Extract listing info from URL params
  const { slug: listingSlug, id: listingId } = params;

  // Retrieve listing from Redux store
  const listingUUID = listingId ? new UUID(listingId) : null;
  const currentListing = ensureListing(getListing(listingUUID));

  const { title = '', publicData = {} } = currentListing.attributes || {};
  const trackingListingProperties = listingMixpanelProperties(currentListing);
  const listingSubtitle = currentListing.attributes?.description || publicData.subtitle || '';

  // Responsive hero images — same screen-size sources as ListingPageCarousel
  const { goldenSection = {}, detailsSection = {} } = publicData;
  let customHeroImageUrl = null;
  let customHeroImageTabletUrl = null;
  let customHeroImageMobileUrl = null;

  if (publicData.screen_experience === 'golden_screen') {
    customHeroImageUrl = detailsSection?.heroImage?.url || goldenSection?.heroImage?.url;
    customHeroImageTabletUrl =
      detailsSection?.heroImageTab?.url || goldenSection?.heroImageTab?.url;
    customHeroImageMobileUrl =
      detailsSection?.heroImageMobile?.url || goldenSection?.heroImageMobile?.url;
  } else {
    customHeroImageUrl = detailsSection?.heroImage?.url;
    customHeroImageTabletUrl = detailsSection?.heroImageTab?.url;
    customHeroImageMobileUrl = detailsSection?.heroImageMobile?.url;
  }

  const heroImageUrl =
    customHeroImageUrl ||
    publicData.heroImageCloudinaryUrl ||
    require('../../assets/listingbackgroundcss.jpg');
  const heroImageTabletUrlOriginal = customHeroImageTabletUrl || heroImageUrl;
  const heroImageMobileUrlOriginal = customHeroImageMobileUrl || heroImageUrl;

  const isCloudinaryHero =
    heroImageUrl && typeof heroImageUrl === 'string' && heroImageUrl.includes('res.cloudinary.com');
  const optimizedHeroImageUrl = isCloudinaryHero
    ? optimizeCloudinaryUrl(heroImageUrl, 1920, '16:9', 'best')
    : heroImageUrl;
  const heroImageSrcSet = isCloudinaryHero
    ? getCloudinarySrcSet(heroImageUrl, '16:9', DESKTOP_HERO_SRCSET_WIDTHS, 'best')
    : null;
  const optimizedCardHeroImageUrl = isCloudinaryHero
    ? optimizeCloudinaryUrl(heroImageUrl, 1200)
    : heroImageUrl;

  const isCloudinaryTablet =
    heroImageTabletUrlOriginal &&
    typeof heroImageTabletUrlOriginal === 'string' &&
    heroImageTabletUrlOriginal.includes('res.cloudinary.com');
  const heroImageTabletUrl = isCloudinaryTablet
    ? optimizeCloudinaryUrl(heroImageTabletUrlOriginal, 1240, '3:4', 'best')
    : heroImageTabletUrlOriginal;
  const heroImageTabletSrcSet = isCloudinaryTablet
    ? getCloudinarySrcSet(heroImageTabletUrlOriginal, '3:4', TABLET_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryMobile =
    heroImageMobileUrlOriginal &&
    typeof heroImageMobileUrlOriginal === 'string' &&
    heroImageMobileUrlOriginal.includes('res.cloudinary.com');
  const heroImageMobileUrl = isCloudinaryMobile
    ? optimizeCloudinaryUrl(heroImageMobileUrlOriginal, 800, '9:16', 'best')
    : heroImageMobileUrlOriginal;
  const heroImageMobileSrcSet = isCloudinaryMobile
    ? getCloudinarySrcSet(heroImageMobileUrlOriginal, '9:16', MOBILE_SRCSET_WIDTHS, 'best')
    : null;

  useEffect(() => {
    if (!currentListing?.id?.uuid || !title) {
      return undefined;
    }

    optionsViewedAtRef.current =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    actionSelectedRef.current = false;
    abandonmentTrackedRef.current = false;

    trackMixpanelEvent(MIXPANEL_EVENTS.BOOKING_OPTIONS_VIEWED, {
      ...trackingListingProperties,
    });

    const trackAbandonment = () => {
      if (actionSelectedRef.current || abandonmentTrackedRef.current) {
        return;
      }
      abandonmentTrackedRef.current = true;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      trackMixpanelEvent(MIXPANEL_EVENTS.FUNNEL_STEP_ABANDONED, {
        ...trackingListingProperties,
        last_step: 'booking_options',
        seconds_on_step: optionsViewedAtRef.current
          ? Math.round((now - optionsViewedAtRef.current) / 1000)
          : null,
      });
    };

    window.addEventListener('pagehide', trackAbandonment);

    return () => {
      window.removeEventListener('pagehide', trackAbandonment);
      trackAbandonment();
    };
  }, [currentListing?.id?.uuid, title]);

  const trackBookingAction = bookingAction => {
    actionSelectedRef.current = true;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const eventProperties = {
      ...trackingListingProperties,
      booking_action: bookingAction,
      seconds_to_decide: optionsViewedAtRef.current
        ? Math.round((now - optionsViewedAtRef.current) / 1000)
        : null,
    };

    trackMixpanelEvent(MIXPANEL_EVENTS.BOOKING_ACTION_SELECTED, eventProperties);
    trackMixpanelEvent(
      bookingAction === 'reserve_experience'
        ? MIXPANEL_EVENTS.RESERVE_EXPERIENCE_CLICKED
        : MIXPANEL_EVENTS.SPEAK_TO_FOUNDER_CLICKED,
      eventProperties
    );
  };

  const handleSpeakToFounder = () => {
    trackBookingAction('speak_to_founder');
    if (listingId && listingSlug) {
      clearReservationContactSession(listingId);
      history.push({
        pathname: `/l/${listingSlug}/${listingId}/reservation-details`,
        state: { bookingType: 'conversation' },
      });
    }
  };

  const handleReserveExperience = () => {
    trackBookingAction('reserve_experience');
    if (listingId && listingSlug) {
      clearReservationContactSession(listingId);
      history.push({
        pathname: `/l/${listingSlug}/${listingId}/booking-account`,
        state: { bookingType: 'reservation' },
      });
    }
  };

  const topbar = <TopbarContainer hideOnScroll={false} />;
  const footer = <FooterContainer />;

  const schemaTitle = intl.formatMessage(
    { id: 'BookingFlowPage.schemaTitle' },
    { marketplaceName: config.marketplaceName }
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={false}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <Helmet>
        <link rel="preconnect" href="https://res.cloudinary.com" />
        {heroImageMobileUrl ? (
          <link
            rel="preload"
            as="image"
            href={heroImageMobileUrl}
            imageSrcSet={heroImageMobileSrcSet || undefined}
            media="(max-width: 600px)"
          />
        ) : null}
        {heroImageTabletUrl ? (
          <link
            rel="preload"
            as="image"
            href={heroImageTabletUrl}
            imageSrcSet={heroImageTabletSrcSet || undefined}
            media="(min-width: 601px) and (max-width: 1240px)"
          />
        ) : null}
        {optimizedHeroImageUrl && typeof optimizedHeroImageUrl === 'string' ? (
          <link
            rel="preload"
            as="image"
            href={optimizedHeroImageUrl}
            imageSrcSet={heroImageSrcSet || undefined}
            media="(min-width: 1241px)"
          />
        ) : null}
      </Helmet>
      <LayoutSingleColumn topbar={topbar} footer={footer}>
        <div className={css.root}>

          {/* Hero Section */}
          <div className={css.staticHeroWrapper}>
            <picture>
              {/* Mobile: 9:16 portrait crop */}
              <source
                media="(max-width: 600px)"
                srcSet={heroImageMobileSrcSet || heroImageMobileUrl}
                sizes="100vw"
                width="600"
                height="1067"
              />
              {/* Tablet: 3:4 portrait crop */}
              <source
                media="(max-width: 1240px)"
                srcSet={heroImageTabletSrcSet || heroImageTabletUrl}
                sizes="100vw"
                width="1240"
                height="1365"
              />
              {/* Desktop fallback: 16:9 landscape */}
              <img
                src={optimizedHeroImageUrl}
                srcSet={heroImageSrcSet}
                sizes="100vw"
                alt={intl.formatMessage({ id: 'ListingPageCarousel.altHeroImage' })}
                className={css.heroImage}
                fetchPriority="high"
                decoding="async"
                width="1920"
                height="1080"
              />
            </picture>

            {/* Overlay */}
            <div className={css.overlay} />

            {/* Content */}
            <div className={css.heroContent}>
              <div className={css.titleLogoline}>{title || <FormattedMessage id="BookingFlowPage.defaultTitle" />}</div>
              <h1 className={css.title}><FormattedMessage id="BookingFlowPage.howToProceed" /></h1>

              <p className={css.subtitle}>
                <FormattedMessage id="BookingFlowPage.journeysSubtitle" />
              </p>
            </div>
          </div>

          <div ref={cardsWrapperRef} className={css.cardsWrapper}>
            <div className={css.cardCircleBg} />
            <div className={css.card}>
              <div className={css.cardImageFounderWrapper}>
                <img
                  src={optimizeCloudinaryUrl(require('../../assets/founderimage2.webp'), 200)}
                  alt="Founder"
                  className={css.cardImageFounder}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className={css.optionContent}>
                <h3 className={css.optionTitle}>
                  <FormattedMessage id="BookingFlowPage.speakToFounderTitle" />
                </h3>
                <p className={css.description}>
                  <FormattedMessage id="BookingFlowPage.speakToFounderDescription" />
                </p>
                <button
                  className={css.optionButton}
                  onClick={handleSpeakToFounder}
                >
                  <FormattedMessage id="BookingFlowPage.scheduleConversationButton" /> <IconCard brand="rightArrow" />
                </button>
              </div>
            </div>

            <div className={css.card}>
              <img
                src={optimizedCardHeroImageUrl}
                alt="Experience"
                className={css.cardImage}
                loading="lazy"
                decoding="async"
              />
              <div className={css.optionContent}>
                <h3 className={css.optionTitle}>
                  <FormattedMessage id="BookingFlowPage.reserveExperienceTitle" />
                </h3>
                <p className={css.description}>
                  <FormattedMessage id="BookingFlowPage.reserveExperienceDescription" />
                </p>
                <button
                  className={css.optionButton}
                  onClick={handleReserveExperience}
                >
                  <FormattedMessage id="BookingFlowPage.continueReservationButton" /> <IconCard brand="rightArrow" />
                </button>
              </div>
            </div>
          </div>

        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user || {};
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    getListing,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
});

const BookingFlowPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(BookingFlowPageComponent);

export default BookingFlowPage;
